<script lang="ts">
  import { board, dayEvents, filters, isPhone, selectedDay } from "../lib/stores";
  import { enumerateDays, formatLongDate, toISO } from "../lib/date";
  import { summarizeDay } from "../lib/vote";
  import { dayMatchesFocus, dayPassesEventFilter, sortDays } from "../lib/derive";
  import { colorStyle, monthHue } from "../lib/dayEvents";
  import { t, localeTag, voteShort } from "../lib/i18n";
  import type { Vote } from "../lib/types";

  // px per day column. Wider on a phone: 46px cannot hold a readable day
  // header, and the header doubles as the tap target that opens the day.
  const COL_W = $derived($isPhone ? 56 : 46);
  const OVERSCAN = 6;

  let scrollLeft = $state(0);
  let viewportW = $state(1200);
  let scrollEl = $state<HTMLElement | null>(null);
  let atStart = $state(true);
  let atEnd = $state(false);

  const focusActive = $derived($filters.focusMembers.length > 0);
  const focusSet = $derived(new Set($filters.focusMembers));

  // In the timeline the focus filter *hides* non-matching days (calendar
  // highlights instead), so you can isolate e.g. "days Rainer can come".
  const days = $derived.by(() => {
    const all = enumerateDays($filters.rangeFrom, $filters.rangeTo);
    const filtered = all.filter((d) => {
      const iso = toISO(d);
      if (!dayPassesEventFilter($dayEvents, iso, $filters.onlyEvents)) return false;
      if (!focusActive) return true;
      return dayMatchesFocus($board.votes, iso, $filters.focusMembers, $filters.focusVote);
    });
    return sortDays(filtered, $board.votes, $filters);
  });

  const total = $derived(days.length);
  const startIndex = $derived(Math.max(0, Math.floor(scrollLeft / COL_W) - OVERSCAN));
  const endIndex = $derived(Math.min(total, Math.ceil((scrollLeft + viewportW) / COL_W) + OVERSCAN));
  const visible = $derived(days.slice(startIndex, endIndex));
  const leftSpacer = $derived(startIndex * COL_W);
  const rightSpacer = $derived(Math.max(0, (total - endIndex) * COL_W));

  function isMonthStart(d: Date): boolean {
    return d.getDate() === 1;
  }

  // Distinct light hue per calendar month so columns stay groupable even when
  // sorted out of date order.
  function hueOf(d: Date): number {
    return monthHue(d.getMonth());
  }

  function voteOf(memberId: string, iso: string): Vote | undefined {
    return $board.votes[iso]?.[memberId];
  }

  function syncEdges(el: HTMLElement) {
    scrollLeft = el.scrollLeft;
    atStart = el.scrollLeft <= 1;
    atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
  }

  function onScroll(e: Event) {
    syncEdges(e.currentTarget as HTMLElement);
  }

  // Turn a normal vertical mouse wheel into horizontal scrolling while the
  // pointer is over the grid, so you can reach the right-hand columns even when
  // the OS hides the horizontal scrollbar (overlay scrollbars).
  function onWheel(e: WheelEvent) {
    if (!scrollEl) return;
    if (scrollEl.scrollWidth <= scrollEl.clientWidth) return; // nothing to pan
    if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return; // already horizontal (trackpad)
    const before = scrollEl.scrollLeft;
    scrollEl.scrollLeft += e.deltaY;
    if (scrollEl.scrollLeft !== before) e.preventDefault(); // don't also scroll the page
  }

  // Nav buttons: jump ~80% of a screen so you can walk across long ranges.
  function nudge(dir: 1 | -1) {
    scrollEl?.scrollBy({ left: dir * Math.max(COL_W * 4, viewportW * 0.8), behavior: "smooth" });
  }
  function jump(dir: 1 | -1) {
    scrollEl?.scrollTo({ left: dir > 0 ? scrollEl.scrollWidth : 0, behavior: "smooth" });
  }

  // Keyboard steering when the grid is focused.
  function onKeydown(e: KeyboardEvent) {
    if (e.key === "ArrowRight") nudge(1);
    else if (e.key === "ArrowLeft") nudge(-1);
    else if (e.key === "Home") jump(-1);
    else if (e.key === "End") jump(1);
    else return;
    e.preventDefault();
  }

  // Click-and-drag to pan (grab the grid and pull). Cells aren't interactive,
  // so this never conflicts with clicks.
  let dragging = $state(false);
  let dragStartX = 0;
  let dragStartLeft = 0;
  /** set once the pointer has travelled far enough to count as a pan, so a day
   * header does not open a modal at the end of a drag */
  let dragMoved = false;
  function onPointerDown(e: PointerEvent) {
    // Touch and pen are left to the browser: the container is already an
    // `overflow: auto` scroller, and capturing those pointers (then calling
    // preventDefault on every move) fought the page's own vertical scrolling.
    if (e.pointerType !== "mouse" || e.button !== 0 || !scrollEl) return;
    dragging = true;
    dragMoved = false;
    dragStartX = e.clientX;
    dragStartLeft = scrollEl.scrollLeft;
    scrollEl.setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: PointerEvent) {
    if (!dragging || !scrollEl) return;
    const dx = e.clientX - dragStartX;
    if (Math.abs(dx) > 4) dragMoved = true;
    scrollEl.scrollLeft = dragStartLeft - dx;
    e.preventDefault();
  }
  function onPointerUp(e: PointerEvent) {
    if (!dragging) return;
    dragging = false;
    try {
      scrollEl?.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer already released */
    }
  }

  /** Open a day from its column header — unless that "click" was really the end
   * of a pan across the grid. */
  function openDay(isoDate: string) {
    if (dragMoved) {
      dragMoved = false;
      return;
    }
    selectedDay.set(isoDate);
  }

  // Recompute edge state whenever the day set changes (range/filter/sort).
  $effect(() => {
    void total;
    if (scrollEl) requestAnimationFrame(() => scrollEl && syncEdges(scrollEl));
  });
</script>

<section class="timeline panel">
  <div class="toolbar">
    <div>
      <p class="eyebrow">{$t("tl.eyebrow")}</p>
      <h2>{$t("tl.peopleDays")}</h2>
    </div>
    <div class="toolbar-right">
      <p class="hint">
        {total === 1 ? $t("tl.day", { n: total }) : $t("tl.days", { n: total })}{focusActive
          ? $t("tl.filtered")
          : ""} · {$t("tl.people", { n: $board.members.length })}
      </p>
      {#if total > 0}
        <div class="scroll-nav">
          <button onclick={() => jump(-1)} disabled={atStart} title={$t("tl.jumpStart")} aria-label={$t("tl.jumpStart")}>⏮</button>
          <button onclick={() => nudge(-1)} disabled={atStart} title={$t("tl.scrollLeft")} aria-label={$t("tl.scrollLeft")}>◀</button>
          <button onclick={() => nudge(1)} disabled={atEnd} title={$t("tl.scrollRight")} aria-label={$t("tl.scrollRight")}>▶</button>
          <button onclick={() => jump(1)} disabled={atEnd} title={$t("tl.jumpEnd")} aria-label={$t("tl.jumpEnd")}>⏭</button>
        </div>
      {/if}
    </div>
  </div>

  {#if total === 0}
    <p class="no-match">{$t("tl.noMatch")}</p>
  {:else}
    <!-- Intentionally an interactive scroll region: drag/wheel/arrow-key panning.
         Accessible alternatives exist (the ⏮◀▶⏭ buttons and focus + arrow keys),
         so the non-interactive-element a11y hints don't apply here. -->
    <!-- svelte-ignore a11y_no_static_element_interactions, a11y_no_noninteractive_tabindex, a11y_no_noninteractive_element_interactions -->
    <div
      class="grid-scroll"
      class:grabbing={dragging}
      role="group"
      aria-label={$t("tl.gridAria")}
      tabindex="0"
      bind:this={scrollEl}
      bind:clientWidth={viewportW}
      onscroll={onScroll}
      onwheel={onWheel}
      onkeydown={onKeydown}
      onpointerdown={onPointerDown}
      onpointermove={onPointerMove}
      onpointerup={onPointerUp}
      onpointercancel={onPointerUp}
    >
      <div class="grid">
        <!-- sticky member-name column -->
        <div class="names">
          <div class="cell head corner">{$t("tl.person")}</div>
          <div class="cell summary-label">{$t("tl.yesResponses")}</div>
          {#each $board.members as m (m.id)}
            <div class="cell name" class:focus={focusSet.has(m.id)} title={m.name}>{m.name}</div>
          {/each}
        </div>

        <div class="spacer" style="width:{leftSpacer}px"></div>

        {#each visible as day (toISO(day))}
          {@const iso = toISO(day)}
          {@const s = summarizeDay($board.votes, iso)}
          {@const ev = $dayEvents[iso]}
          <div class="col" class:month-start={isMonthStart(day)} style="width:{COL_W}px">
            <!-- The whole header is the tap target that opens the day. It used
                 to be a 12x5px event dot, which no thumb can hit — and days
                 without an event were a dead end in this view entirely. -->
            <button
              class="cell head day"
              style="--mh:{hueOf(day)}"
              title={formatLongDate(day, $localeTag)}
              aria-label={formatLongDate(day, $localeTag)}
              onclick={() => openDay(iso)}
            >
              <span class="mon">{day.toLocaleDateString($localeTag, { month: "short" })}</span>
              <span class="dnum">{day.getDate()}</span>
              <span class="dow">{day.toLocaleDateString($localeTag, { weekday: "narrow" })}</span>
              {#if ev}
                <span class="ev-dot" style={colorStyle(ev.color)} title={ev.title}></span>
              {/if}
            </button>
            <div class="cell summary">{s.yes}/{s.total}</div>
            {#each $board.members as m (m.id)}
              {@const v = voteOf(m.id, iso)}
              <div class="cell vote {v ?? 'empty'}">{v ? $voteShort[v] : ""}</div>
            {/each}
          </div>
        {/each}

        <div class="spacer" style="width:{rightSpacer}px"></div>
      </div>
    </div>
  {/if}
</section>

<style>
  .timeline {
    padding: 18px;
    /* grid items default to min-width:auto, which let the panel grow to the full
       (very wide) grid and overflow the whole page instead of scrolling inside.
       Pinning min-width:0 keeps the panel at its column width so .grid-scroll
       actually scrolls horizontally within it. */
    min-width: 0;
  }
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
  }
  .hint {
    color: var(--muted);
    font-size: 13px;
    font-weight: 700;
  }
  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  .scroll-nav {
    display: flex;
    gap: 4px;
    padding: 3px;
    border: 1px solid var(--line);
    border-radius: 10px;
    background: var(--surface-a);
  }
  .scroll-nav button {
    min-width: 30px;
    height: 28px;
    border: 0;
    border-radius: 7px;
    background: transparent;
    color: var(--ink);
    font-size: 12px;
    font-weight: 800;
  }
  .scroll-nav button:hover:not(:disabled) {
    background: var(--chip);
  }
  .scroll-nav button:disabled {
    opacity: 0.32;
    cursor: default;
  }
  /* NOT `.empty`: an unvoted grid cell is `.cell.vote.empty`, so this rule's
     padding was landing on every blank cell in the grid and making it 48px
     tall against the 38px of its voted neighbours — which knocked whole
     columns out of alignment. */
  .no-match {
    color: var(--muted);
    padding: 24px 0;
  }
  .grid-scroll {
    overflow: auto;
    max-height: 72vh;
    border: 1px solid var(--line);
    border-radius: 16px;
    background: var(--surface);
    cursor: grab;
    /* Keep a real, always-visible horizontal scrollbar inside the panel rather
       than relying on the OS overlay bar (which hides itself and made it hard
       to reach the right-hand columns). */
    scrollbar-width: thin;
    scrollbar-color: var(--muted) transparent;
    overscroll-behavior-x: contain;
  }
  .grid-scroll.grabbing {
    cursor: grabbing;
    user-select: none;
  }
  .grid-scroll:focus-visible {
    outline: 2px solid var(--yes);
    outline-offset: 2px;
  }
  .grid-scroll::-webkit-scrollbar {
    height: 12px;
  }
  .grid-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  .grid-scroll::-webkit-scrollbar-thumb {
    background: var(--line);
    border-radius: 999px;
    border: 3px solid var(--surface);
  }
  .grid-scroll::-webkit-scrollbar-thumb:hover {
    background: var(--muted);
  }
  .grid {
    display: flex;
    align-items: flex-start;
    width: max-content;
  }
  .names {
    position: sticky;
    left: 0;
    z-index: 3;
    flex: 0 0 168px;
    width: 168px;
    background: var(--header);
    box-shadow: 2px 0 0 var(--line);
  }
  .col {
    flex: 0 0 auto;
  }
  .col.month-start {
    box-shadow: inset 2px 0 0 var(--maybe);
  }
  .spacer {
    flex: 0 0 auto;
  }
  .cell {
    height: 38px;
    min-height: 38px;
    max-height: 38px;
    /* explicit, because padding is NOT compressible: with box-sizing:border-box
       a stray padding still floors the used height above max-height, and every
       row in this grid has to line up to the pixel. */
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    /* row separator painted with box-shadow, not border: a border participates
       in the box height and its sub-pixel rounding accumulates down a column,
       which shifted the lower rows a few px out of alignment on some displays */
    box-shadow: inset 0 -1px 0 var(--line);
    font-size: 12px;
    font-weight: 800;
  }
  .cell.head {
    height: 56px;
    min-height: 56px;
    max-height: 56px;
    position: sticky;
    top: 0;
    z-index: 2;
    background: var(--header);
    flex-direction: column;
    gap: 0;
  }
  /* the day header is a <button>; strip the UA chrome so it still reads as a
     grid cell, and keep every box metric identical to the divs around it */
  button.cell.head.day {
    width: 100%;
    border: 0;
    padding: 0;
    font: inherit;
    color: inherit;
    text-align: center;
  }
  /* Per-month tinted day header (readable even when sorted out of date order).
     Mixed into the surface rather than a fixed light hsl(), so it works in every
     palette — and it shares the hue scale with the calendar's month cards. */
  .col .cell.head.day {
    --mtint: hsl(var(--mh, 40) 60% 50%);
    background: color-mix(in srgb, var(--mtint) calc(var(--month-mix) * 2), var(--header));
    box-shadow: inset 0 -2px 0 color-mix(in srgb, var(--mtint) 45%, var(--line));
  }
  /* month hue as a tint of --ink, not the other way round: at >45% accent the
     label falls under 4.5:1 on its own tinted header */
  .day .mon {
    color: color-mix(in srgb, hsl(var(--mh, 40) 60% 50%) 38%, var(--ink));
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }
  .names .corner {
    z-index: 4;
    /* column-flex header: bottom-left so "Person" sits just above the names,
       aligned with the day-number baseline instead of floating up top-centre */
    align-items: flex-start;
    justify-content: flex-end;
    padding: 0 12px 8px;
    color: var(--muted);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .summary-label,
  .name {
    justify-content: flex-start;
    padding-left: 12px;
  }
  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    line-height: 38px;
  }
  .name.focus {
    color: var(--yes-ink);
    background: var(--yes-soft);
  }
  .summary-label {
    color: var(--muted);
    font-size: 11px;
    background: var(--surface-2);
  }
  .summary {
    color: var(--muted);
    background: var(--surface-2);
  }
  .day .dnum {
    font-size: 13px;
    font-weight: 800;
  }
  /* --muted is tuned against --surface, not against the tinted day header */
  .day .dow {
    color: color-mix(in srgb, var(--muted) 55%, var(--ink));
    font-weight: 700;
    font-size: 10px;
  }
  /* small colour flag on days that hold an event; the column is only 46px wide,
     so it sits under the weekday letter rather than beside it */
  .ev-dot {
    width: 12px;
    height: 5px;
    margin-top: 1px;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: var(--ev);
  }
  /* widen, never grow taller: the header is pinned to a fixed height and every
     column has to keep the same row alignment */
  .day:hover .ev-dot {
    width: 18px;
  }
  .vote {
    color: var(--muted);
  }
  .vote.yes {
    color: var(--on-yes);
    background: var(--yes);
  }
  .vote.maybe {
    color: var(--on-maybe);
    background: var(--maybe);
  }
  .vote.no {
    color: var(--on-no);
    background: var(--no);
  }
  .vote.empty {
    background: var(--surface);
  }

  @media (max-width: 760px) {
    .timeline {
      padding: 12px;
    }
    .toolbar {
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
    }
    .toolbar-right {
      justify-content: space-between;
    }
    .hint {
      font-size: 14px;
    }
    /* the pan buttons are the only way across the grid without a mouse wheel */
    .scroll-nav button {
      min-width: var(--tap);
      height: var(--tap);
      font-size: 14px;
    }
    .names {
      flex-basis: 116px;
      width: 116px;
    }
    /* Taller rows and bigger type: at 38px/10px this grid was legible on a
       monitor and not in a hand. Heights stay pinned to the pixel so the sticky
       name column keeps lining up with its row. */
    .cell {
      height: 44px;
      min-height: 44px;
      max-height: 44px;
      font-size: 13px;
    }
    .name {
      line-height: 44px;
      font-size: 13px;
    }
    .cell.head {
      height: 62px;
      min-height: 62px;
      max-height: 62px;
    }
    .day .mon,
    .day .dow {
      font-size: 12px;
    }
    .day .dnum {
      font-size: 15px;
    }
    .names .corner,
    .summary-label {
      font-size: 12.5px;
    }
    /* leave room for the fixed bottom nav under the grid */
    .grid-scroll {
      max-height: min(66vh, 100svh - var(--nav-h) - 250px);
    }
  }

  /* At 320px a 116px name column leaves room for two days. Names ellipsis
     anyway (they have a title attribute), so give the width to the votes. */
  @media (max-width: 360px) {
    .names {
      flex-basis: 92px;
      width: 92px;
    }
    .summary-label,
    .name {
      padding-left: 9px;
    }
  }
</style>
